import React, { useMemo } from "react";

import { GenericTooltip, HTMLTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { DataTypeProvider } from "@devexpress/dx-react-grid";
import {
  Grid,
  TableHeaderRow,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import {
  type GetSubLevelBreakdown_me_adObject_subLevelBreakdown_rows,
  makeFlyoutPerformanceRows,
} from "@north-beam/nb-client-side";
import {
  type ColumnDef,
  createMetricColumns,
} from "@pages/sales/tab-table-section/table/table-utils";

import { type FlyoutConfig } from "../flyout";

import { HeadComponent } from "./head-component";
import { type SubLevel } from "./types";
import { processForTable } from "./utils";

interface Props {
  rows: GetSubLevelBreakdown_me_adObject_subLevelBreakdown_rows[];
  adObjectLevel: SubLevel;
  config: FlyoutConfig;
  onClickAdObject(id: string): void;
}

export const Table = ({
  rows,
  adObjectLevel,
  config,
  onClickAdObject,
}: Props) => {
  const { user } = useUser();
  const metricsArray = useMemo(
    () =>
      makeFlyoutPerformanceRows(
        config.attributionModel,
        config.attributionWindow,
        config.accountingMode,
        config.timeGranularity,
        user,
      ),
    [config, user],
  );
  const metricColumns = useMemo(
    () => createMetricColumns(metricsArray),
    [metricsArray],
  );
  const columnConfigs = useMemo(
    () => [
      {
        name: "Name",
        id: "name",
        title: "",
        width: 450,
      },
      ...metricColumns,
    ],
    [metricColumns],
  );

  const processedData = useMemo(
    () =>
      processForTable(
        rows,
        metricsArray,
        config.dateRangeInterval,
        user.currencies.currentCurrency,
      ),
    [rows, metricsArray, config],
  );

  return (
    <Grid
      rootComponent={(props) => (
        <Grid.Root
          {...props}
          style={{ height: "100%" }}
          className="flyout-objects-table-v3"
        />
      )}
      columns={columnConfigs}
      rows={processedData.rest.sort(
        (a, b) => b.metrics.spend - a.metrics.spend,
      )}
      getRowId={(row) => row.key}
    >
      <VirtualTable
        height="auto"
        estimatedRowHeight={44}
        headComponent={(props) => (
          <HeadComponent
            {...props}
            adObjectLevel={adObjectLevel}
            columnConfigs={columnConfigs}
            row={processedData.total}
          />
        )}
        columnExtensions={columnConfigs.map((e) => {
          const width = { Name: 180, Spend: 120, ROAS: 80 }[e.name] ?? 140;
          return { columnName: e.name, width };
        })}
      />
      <TableHeaderRow
        cellComponent={({ children }) => (
          <th
            className="position-relative dx-g-bs4-header-cell text-nowrap"
            scope="col"
            colSpan={1}
          >
            {children}
          </th>
        )}
        contentComponent={({ column }) => {
          const col = column as ColumnDef;

          return (
            <>
              {col.tooltip ? (
                <HTMLTooltip
                  className="overflow-hidden text-ellipsis w-full"
                  html={col.tooltip}
                  noInfoCircle
                >
                  {col.title}
                </HTMLTooltip>
              ) : (
                col.title
              )}
            </>
          );
        }}
      />
      <DataTypeProvider
        for={["Name"]}
        formatterComponent={(props) => (
          <NameDataFormatter {...props} onClick={onClickAdObject} />
        )}
      />
      <DataTypeProvider
        for={columnConfigs.filter((e) => e.name !== "Name").map((e) => e.name)}
        formatterComponent={MetricDataFormatter}
      />
    </Grid>
  );
};

const NameDataFormatter = ({
  row,
  onClick,
}: DataTypeProvider.ValueFormatterProps & {
  onClick: (id: string) => void;
}) => {
  return (
    <div className="cursor-pointer truncate" onClick={() => onClick(row.id)}>
      <GenericTooltip noInfoCircle content={row.name} mouseEnterDelay={0.5}>
        {row.name}
      </GenericTooltip>
    </div>
  );
};

const MetricDataFormatter = ({
  row,
  column,
}: DataTypeProvider.ValueFormatterProps) => {
  return row.display[(column as ColumnDef).id];
};
